import { Flight } from "../../gol-api/types/flightSearch";
import {
  BrandDetails,
  BrandIncludedType,
  BrandService,
} from "../../types/brands";
import createFilteredServicesList from "./createFilteredServicesList";

const listFilterableServicesForBrand = (
  brandServices: BrandService[],
  filteredType: BrandIncludedType
): Array<string> => {
  return brandServices.reduce((acc, service) => {
    if (service.Included === filteredType) {
      acc.push(service.Type);
    }
    return acc;
  }, []);
};

/**
 * Creates data for Branded Fares Brands table - data for each brand and its services
 * also creates list of service names that should be filtered from view
 * injects flightCombination (needed to pick branded flight) and PricingDetail for each brand
 */
const createBrandDetailsData = (
  requestData: Flight,
  filterUnknownServices: boolean,
  filterNotIncludedServices: boolean,
  filterMixedServices: boolean
): { brandsDetailsData: BrandDetails[]; filteredServiceNames: string[] } => {
  const unknownServices = [];
  const notIncludedServices = [];
  const mixedServices = [];
  let filteredServiceNames = null;

  if (!("UniformBrand" in requestData.UniformBrands)) {
    throw new Error(
      "no UniformBrands information can be found in brands detail request"
    );
  }

  const brandsDetailsData = requestData.UniformBrands.UniformBrand.map(
    (brand) => {
      if (filterUnknownServices) {
        unknownServices.push(
          listFilterableServicesForBrand(
            brand.BrandServices.BrandService,
            "Unknown"
          )
        );
      }

      if (filterNotIncludedServices) {
        notIncludedServices.push(
          listFilterableServicesForBrand(brand.BrandServices.BrandService, "No")
        );
      }

      if (filterMixedServices) {
        mixedServices.push(
          listFilterableServicesForBrand(
            brand.BrandServices.BrandService,
            "Mixed"
          )
        );
      }

      const originalBrand = requestData.Brands?.Brand?.find(
        (originalBrandItem) => originalBrandItem.Name === brand.UniformBrandName
      );

      const uniformServices = brand.BrandServices.BrandService;
      const originalServices = originalBrand?.BrandServices?.BrandService || [];
      const mergedBrandService = uniformServices.map((uniformService) => {
        const originalService = originalServices.find(
          (s) => s.Type === uniformService.Type
        );
        return { ...originalService, ...uniformService } as BrandService;
      });
      const brandServices = { BrandService: mergedBrandService };

      const brandFlightCombination = requestData.FlightCombinations.FlightCombination.find(
        (combination) => {
          return combination.UniformBrandName === brand.UniformBrandName;
        }
      );

      // gets pricing infos for current brand
      const brandPricingDetail = (
        requestData?.AlternativeCurrencyPricingDetails
          ?.AlternativeCurrencyPricingDetail ||
        requestData.PricingDetails.PricingDetail
      ).find((pricingDetail) => {
        return pricingDetail.Key === brandFlightCombination.PricingDetailKey;
      });
      brandFlightCombination.PricingDetail = brandPricingDetail;

      return {
        ...brand,
        BrandServices: brandServices,
        flightCombination: brandFlightCombination,
        PricingDetail: brandPricingDetail,
      };
    }
  );

  if (filterUnknownServices || filterNotIncludedServices) {
    filteredServiceNames = createFilteredServicesList(
      filterUnknownServices,
      filterNotIncludedServices,
      filterMixedServices,
      unknownServices,
      notIncludedServices,
      mixedServices
    );
  }

  return { brandsDetailsData, filteredServiceNames };
};

export default createBrandDetailsData;
